import { IconProp } from "@fortawesome/fontawesome-svg-core";
import DocumentSimplifiedDTO from "features/invoices/dtos/DocumentSimplifiedDTO";
import React from "react";
import RoundButton from "features/shared/components/buttons/RoundButton";
import { useInjection } from "infrastructure/ioc/ioc.react";
import IFileService from "data/IFileService";
import { IocTypes } from "infrastructure/ioc/ioc.types";
import { Dropdown } from "react-bootstrap";
import RoundDropdownButton from "features/shared/components/buttons/RoundDropdownButton";
import DropdownActionItem from "features/shared/components/dropdown/DropdownActionItem";
import ExportDefinitionSimplifiedDTO from "features/exports/dtos/ExportDefinitionSimplifiedDTO";
import { useExport } from "features/exports/hooks/useExport";

type Props = {
  id: string;
  icon: IconProp;
  documents: DocumentSimplifiedDTO[];
  exports?: ExportDefinitionSimplifiedDTO[];
  dropdownClass?: string;
  wrapText?: boolean;
  formatDocumentTitle?: (document: DocumentSimplifiedDTO) => string;
};

const DocumentDownloadButton = ({
  id,
  icon,
  documents,
  exports,
  dropdownClass,
  wrapText,
  formatDocumentTitle: formatTitle
}: Props) => {
  // DI
  const fileService = useInjection<IFileService>(IocTypes.FileService);
  // export hook
  const { exportHandler } = useExport({});

  // hadlers
  const fileDownloadHandler = (document: DocumentSimplifiedDTO) => {
    fileService.downloadDocument(document);
  };

  const exportDownloadHandler = (exp: ExportDefinitionSimplifiedDTO) => {
    if (exp?.exportDefinitionId && exp?.exportParams) {
      exportHandler(exp.exportDefinitionId, exp.exportParams);
    }
  };

  // helpers
  const getDownloadTitle = (document: DocumentSimplifiedDTO): string => {
    if (formatTitle) {
      return formatTitle(document);
    } else {
      return document.name!;
    }
  };

  // sub-renders
  const renderDownloadActions = () => {
    return documents.map((item, index) => (
      <DropdownActionItem
        title={getDownloadTitle(item)}
        icon="download"
        onClick={() => fileDownloadHandler(item)}
        key={`doc-${index}`}
        wrapText={wrapText}
      />
    ));
  };

  const renderExportActions = () => {
    if (!exports || exports.length === 0) return null;
    return exports.map((exp, index) => (
      <DropdownActionItem
        title={exp.name ?? ""}
        icon="download"
        onClick={() => exportDownloadHandler(exp)}
        key={`exp-${exp.exportDefinitionId ?? index}`}
        wrapText={wrapText}
      />
    ));
  };

  // render
  const docsLen = documents?.length ?? 0;
  const expsLen = exports?.length ?? 0;

  // if any exports exist, always show dropdown and order: documents first, then exports
  if (expsLen > 0) {
    return (
      <Dropdown>
        <RoundDropdownButton
          menuId={`document-download-button-menu-${id}`}
          icon={icon}
        />
        <Dropdown.Menu className={dropdownClass}>
          {docsLen > 0 && renderDownloadActions()}
          {renderExportActions()}
        </Dropdown.Menu>
      </Dropdown>
    );
  } else if (docsLen > 1) {
    return (
      <Dropdown>
        <RoundDropdownButton
          menuId={`document-download-button-menu-${id}`}
          icon={icon}
        />
        <Dropdown.Menu className={dropdownClass}>
          {renderDownloadActions()}
        </Dropdown.Menu>
      </Dropdown>
    );
  } else if (docsLen === 1) {
    return (
      <RoundButton
        icon={icon}
        onClickHandler={() => fileDownloadHandler(documents[0])}
      />
    );
  } else {
    return <></>;
  }
};

export default DocumentDownloadButton;
